import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import Paywall from '../components/Paywall';
import CountrySelect from '../components/CountrySelect';
import { useSubscription } from '../components/SubscriptionContext';
import { sendSms, scheduleSms, primeSmsToken } from '../services/notificationApi';
import { DEFAULT_COUNTRY } from '../constants/countries';
import { ensureCountryCode } from '../utils/phone';
import './Services.css';

const timezoneOptions = [
  'UTC',
  'Asia/Kolkata',
  'Europe/London',
  'America/New_York',
  'Asia/Dubai',
  'Australia/Sydney',
];

const smsFeatures = [
  'Send SMS instantly with status banners',
  'Schedule messages with timezone support',
  'Razorpay-guarded access per email',
];

const Sms = () => {
  const { user } = useAuth0();
  const email = user?.email;
  const { isSubscribedForEmail, isFeatureUnlocked, subscribeForFeature } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [scheduleTo, setScheduleTo] = useState('');
  const [scheduleMessage, setScheduleMessage] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleTimezone, setScheduleTimezone] = useState(() => {
    const tz = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone;
    return timezoneOptions.includes(tz) ? tz : 'UTC';
  });
  const [isSending, setIsSending] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [activeTab, setActiveTab] = useState('send');
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState(null); // 'success' | 'error'
  const [sendCountry, setSendCountry] = useState(DEFAULT_COUNTRY);
  const [scheduleCountry, setScheduleCountry] = useState(DEFAULT_COUNTRY);

  const isSubscribed = isSubscribedForEmail(email) || isFeatureUnlocked('sms');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    if (mode === 'schedule') {
      setActiveTab('schedule');
    } else {
      setActiveTab('send');
    }
  }, [location.search]);

  useEffect(() => {
    if (user) {
      primeSmsToken().catch((err) => console.error('SMS token warm-up failed', err));
    }
  }, [user]);

  if (!isSubscribed) {
    return (
      <div className="service-page">
        <Paywall
          title="Unlock SMS"
          description="Subscribe for ₹5 to send or schedule SMS with status updates."
          onSubscribe={() => subscribeForFeature('sms')}
          ctaLabel="Subscribe for ₹5"
          priceLabel="₹5"
          features={smsFeatures}
        />
      </div>
    );
  }

  const handleSend = async (e) => {
    e.preventDefault();
    try {
      setIsSending(true);
      setStatusMessage('');
      setStatusType(null);
      const normalizedTo = ensureCountryCode(to, sendCountry?.dialCode || DEFAULT_COUNTRY.dialCode);
      const result = await sendSms(normalizedTo, message);
      setStatusType('success');
      setStatusMessage(result?.message || 'SMS delivered successfully');
      toast.success(result?.message || 'SMS delivered successfully');
    } catch (err) {
      console.error(err);
      setStatusType('error');
      setStatusMessage(`Failed to send SMS: ${err.message}`);
      toast.error(`Failed to send SMS: ${err.message}`);
    } finally {
      setIsSending(false);
    }
  };

  const handleSchedule = (e) => {
    e.preventDefault();
    const toSend = async () => {
      try {
        setIsScheduling(true);
        setStatusMessage('');
        setStatusType(null);

        // Format local datetime to 24h ISO-like string without timezone shift
        const dt = new Date(scheduleTime);
        if (Number.isNaN(dt.getTime())) throw new Error('Invalid date/time');
        const pad = (n) => String(n).padStart(2, '0');
        const localDateTime = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;

        const normalizedTo = ensureCountryCode(scheduleTo, scheduleCountry?.dialCode || DEFAULT_COUNTRY.dialCode);
        const result = await scheduleSms(normalizedTo, scheduleMessage, localDateTime, scheduleTimezone);
        setStatusType('success');
        setStatusMessage(result?.message || 'SMS scheduled successfully');
        toast.success(result?.message || 'SMS scheduled successfully');
      } catch (err) {
        console.error(err);
        setStatusType('error');
        setStatusMessage(`Failed to schedule SMS: ${err.message}`);
        toast.error(`Failed to schedule SMS: ${err.message}`);
      } finally {
        setIsScheduling(false);
      }
    };

    toSend();
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(location.search);
    params.set('mode', tab === 'schedule' ? 'schedule' : 'send');
    navigate({ pathname: '/dashboard/sms', search: params.toString() }, { replace: true });
  };

  return (
    <div className="service-page">
      <div className="service-heading">
        <div>
          <h1>SMS</h1>
          <p className="service-description">Unlock for ₹5 to send or schedule SMS messages.</p>
        </div>
        <div className="service-actions">
          <button
            type="button"
            className={`tab-btn ${activeTab === 'send' ? 'active' : ''}`}
            onClick={() => switchTab('send')}
          >
            Send SMS
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === 'schedule' ? 'active' : ''}`}
            onClick={() => switchTab('schedule')}
          >
            Schedule SMS
          </button>
        </div>
      </div>

      {activeTab === 'send' && (
        <div className="service-grid">
          <form className="service-form" onSubmit={handleSend}>
            <h2>Send SMS now</h2>
            <label>
              To (phone number)
              <div className="phone-input-group">
                <CountrySelect
                  value={sendCountry}
                  onChange={setSendCountry}
                  ariaLabel="Select country for SMS"
                  selectId="sms-country-select"
                />
                <input
                  type="tel"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="XXXXXXXXXX"
                  required
                />
              </div>
            </label>
            <label>
              Message
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} required />
            </label>
            <button type="submit" className="primary-btn" disabled={isSending}>{isSending ? 'Sending…' : 'Send SMS'}</button>
            {statusMessage && (
              <div className={`status-banner ${statusType || ''}`}>
                {statusType === 'success' && <span className="status-icon check">✓</span>}
                {statusType === 'error' && <span className="status-icon error">!</span>}
                <span>{statusMessage}</span>
              </div>
            )}
          </form>
        </div>
      )}

      {activeTab === 'schedule' && (
        <div className="service-grid">
          <form className="service-form" onSubmit={handleSchedule}>
            <h2>Schedule SMS</h2>
            <label>
              To (phone number)
              <div className="phone-input-group">
                <CountrySelect
                  value={scheduleCountry}
                  onChange={setScheduleCountry}
                  ariaLabel="Select country for scheduled SMS"
                  selectId="schedule-sms-country-select"
                />
                <input
                  type="tel"
                  value={scheduleTo}
                  onChange={(e) => setScheduleTo(e.target.value)}
                  placeholder="XXXXXXXXXX"
                  required
                />
              </div>
            </label>
            <label>
              Message
              <textarea value={scheduleMessage} onChange={(e) => setScheduleMessage(e.target.value)} required />
            </label>
            <label>
              Send at
              <input type="datetime-local" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} required />
            </label>
            <label>
              Timezone
              <select value={scheduleTimezone} onChange={(e) => setScheduleTimezone(e.target.value)} required>
                {timezoneOptions.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </label>
            <button type="submit" className="primary-btn" disabled={isScheduling}>{isScheduling ? 'Scheduling…' : 'Schedule SMS'}</button>
            {statusMessage && (
              <div className={`status-banner ${statusType || ''}`}>
                {statusType === 'success' && <span className="status-icon check">✓</span>}
                {statusType === 'error' && <span className="status-icon error">!</span>}
                <span>{statusMessage}</span>
              </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
};

export default Sms;
